/**
 * Where the visitor is, and their money — for public pages (landing,
 * pricing, sign-up) before anyone has a business.
 *
 *   const { country, currency, planRate } = useVisitorLocale();
 *
 * Order: the country they picked on this device → GET /public/locale (the
 * host's geo header, else browser language) → the browser's time zone
 * (Africa/Lagos → NG). Nothing about the visitor is sent anywhere else.
 */
import { useSyncExternalStore } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/services/api";
import { COUNTRIES } from "@/lib/currency";

export interface VisitorLocale {
  country: string | null;
  currency: string | null;
  planRate: number | null; // 1 GHS in their currency
  rateIsEstimate: boolean;
  source: "chosen" | "network" | "browser" | "timezone" | "unknown";
}

const PICK_KEY = "eaze:visitor-country";

const TZ_COUNTRY: Record<string, string> = {
  "Africa/Accra": "GH", "Africa/Lagos": "NG", "Africa/Nairobi": "KE", "Africa/Johannesburg": "ZA", "Africa/Kampala": "UG",
  "Africa/Dar_es_Salaam": "TZ", "Africa/Kigali": "RW", "Africa/Abidjan": "CI", "Africa/Dakar": "SN", "Africa/Porto-Novo": "BJ",
  "Africa/Lome": "TG", "Africa/Ouagadougou": "BF", "Africa/Bamako": "ML", "Africa/Niamey": "NE", "Africa/Douala": "CM",
  "Africa/Libreville": "GA", "Africa/Brazzaville": "CG", "Africa/Kinshasa": "CD", "Africa/Lubumbashi": "CD", "Africa/Addis_Ababa": "ET",
  "Africa/Cairo": "EG", "Africa/Casablanca": "MA", "Africa/Lusaka": "ZM", "Africa/Blantyre": "MW", "Africa/Maputo": "MZ",
  "Africa/Luanda": "AO", "Africa/Harare": "ZW", "Africa/Gaborone": "BW", "Africa/Windhoek": "NA", "Africa/Freetown": "SL",
  "Africa/Monrovia": "LR", "Africa/Banjul": "GM", "Africa/Conakry": "GN", "Indian/Mauritius": "MU", "Indian/Antananarivo": "MG",
};

export function readPickedCountry(): string | null {
  try {
    const v = localStorage.getItem(PICK_KEY);
    return v && COUNTRIES.some((c) => c.code === v) ? v : null;
  } catch {
    return null;
  }
}

const listeners = new Set<() => void>();
let memoryPick: string | null = null; // when storage is blocked

export function pickCountry(code: string | null) {
  memoryPick = code;
  try {
    if (code) localStorage.setItem(PICK_KEY, code);
    else localStorage.removeItem(PICK_KEY);
  } catch {
    /* private mode — memoryPick keeps it for this visit */
  }
  listeners.forEach((l) => l());
}

function usePickedCountry(): string | null {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => readPickedCountry() ?? memoryPick,
    () => null,
  );
}

function timezoneCountry(): string | null {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return TZ_COUNTRY[tz] || null;
  } catch {
    return null;
  }
}

export const visitorKey = (picked: string | null) => ["public", "locale", picked] as const;

export function useVisitorLocale(override?: string | null, enabled = true) {
  const stored = usePickedCountry();
  const picked = override ?? stored;
  return useQuery<VisitorLocale>({
    queryKey: visitorKey(picked),
    enabled,
    staleTime: 60 * 60 * 1000,
    retry: false,
    queryFn: async () => {
      const ask = async (country?: string | null) => (await api.get<{ data: VisitorLocale }>("/public/locale", { params: country ? { country } : {} })).data.data;
      try {
        if (picked) return { ...(await ask(picked)), source: "chosen" };
        const d = await ask();
        // The network's country is best; the device's time zone beats the
        // browser language (lots of phones in Accra say en-US).
        if (d.country && d.source === "network") return d;
        const tz = timezoneCountry();
        return tz ? { ...(await ask(tz)), source: "timezone" } : d;
      } catch {
        const guess = picked || timezoneCountry();
        const c = COUNTRIES.find((x) => x.code === guess);
        return { country: c?.code || null, currency: c?.currency || null, planRate: c?.currency === "GHS" ? 1 : null, rateIsEstimate: true, source: guess ? "timezone" : "unknown" };
      }
    },
  });
}
